<script>
import { mapGetters } from 'vuex';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import UpdateBanner from './components/app/UpdateBanner.vue';
import StatusBanner from './components/app/StatusBanner.vue';
import SessionTimeoutBanner from './components/app/SessionTimeoutBanner.vue';
import SingleTabBlocker from './components/app/SingleTabBlocker.vue';
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
import LowBackupCodesBanner from './components/app/LowBackupCodesBanner.vue';
import vueActionCable from './helper/actionCable';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import { setColorTheme } from './helper/themeHelper';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFontSize } from 'dashboard/composables/useFontSize';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';
import ReconnectService from 'dashboard/helper/ReconnectService';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { setRuntimeLocale } from 'shared/helpers/timeHelper';
import Auth from 'dashboard/api/auth';
import { clearCookiesOnLogout } from 'dashboard/store/utils/api';
import {
  createIdleSessionManager,
  resolveIdleLogoutMinutes,
} from 'dashboard/helper/idleSessionManager';
import { createSingleActiveTabManager } from 'dashboard/helper/singleActiveTabManager';

export default {
  name: 'App',

  components: {
    LoadingState,
    NetworkNotification,
    UpdateBanner,
    StatusBanner,
    SessionTimeoutBanner,
    SingleTabBlocker,
    PaymentPendingBanner,
    WootSnackbarBox,
    PendingEmailVerificationBanner,
    LowBackupCodesBanner,
  },
  setup() {
    const router = useRouter();
    const store = useStore();
    const { accountId } = useAccount();
    // Use the font size composable (it automatically sets up the watcher)
    const { currentFontSize } = useFontSize();
    const { uiSettings } = useUISettings();

    return {
      router,
      store,
      currentAccountId: accountId,
      currentFontSize,
      uiSettings,
    };
  },
  data() {
    return {
      latestChatwootVersion: null,
      reconnectService: null,
      idleSessionManager: null,
      showSessionTimeoutBanner: false,
      sessionTimeoutRemainingMs: 0,
      idleLogoutInProgress: false,
      singleActiveTabManager: null,
      isBlockedByAnotherTab: false,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      isRTL: 'accounts/isRTL',
      currentUser: 'getCurrentUser',
      authUIFlags: 'getAuthUIFlags',
      globalConfig: 'globalConfig/get',
    }),
    hideOnOnboardingView() {
      return !isOnOnboardingView(this.$route);
    },
    idleLogoutMinutes() {
      return resolveIdleLogoutMinutes(this.globalConfig?.idleLogoutMinutes);
    },
    shouldEnableIdleLogout() {
      return (
        this.idleLogoutMinutes > 0 &&
        !this.isBlockedByAnotherTab &&
        !this.authUIFlags.isFetching &&
        !!this.currentUser?.id
      );
    },
    shouldEnableSingleActiveTabGuard() {
      return (
        !!this.globalConfig?.singleActiveDashboardTab &&
        !this.authUIFlags.isFetching &&
        !!this.currentUser?.id
      );
    },
  },

  watch: {
    currentAccountId: {
      immediate: true,
      handler() {
        if (this.currentAccountId) {
          this.initializeAccount();
        }
      },
    },
    shouldEnableIdleLogout: {
      immediate: true,
      handler(shouldEnable) {
        if (shouldEnable) {
          this.setupIdleSessionManager();
          return;
        }

        this.destroyIdleSessionManager();
      },
    },
    shouldEnableSingleActiveTabGuard: {
      immediate: true,
      handler(shouldEnable) {
        if (shouldEnable) {
          this.setupSingleActiveTabGuard();
          return;
        }

        this.destroySingleActiveTabGuard();
      },
    },
    '$route.fullPath'() {
      this.idleSessionManager?.markActive({ forceSync: true });
    },
  },
  mounted() {
    this.initializeColorTheme();
    this.listenToThemeChanges();
    // If user locale is set, use it; otherwise use account locale
    this.setLocale(
      this.uiSettings?.locale || window.chatwootConfig.selectedLocale
    );
  },
  unmounted() {
    if (this.reconnectService) {
      this.reconnectService.disconnect();
    }
    this.destroyIdleSessionManager();
    this.destroySingleActiveTabGuard();
  },
  methods: {
    initializeColorTheme() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches);
    },
    setLocale(locale) {
      const resolvedLocale =
        locale ||
        window.chatwootConfig?.selectedLocale ||
        document?.documentElement?.lang ||
        'es';

      const assignLocale = target => {
        if (!target) return;

        if (typeof target.locale === 'string') {
          target.locale = resolvedLocale;
          return;
        }

        if (target.locale && typeof target.locale === 'object') {
          target.locale.value = resolvedLocale;
        }
      };

      setRuntimeLocale(resolvedLocale);
      assignLocale(this.$i18n);
      assignLocale(this.$root?.$i18n);

      if (window.chatwootConfig) {
        window.chatwootConfig.selectedLocale = resolvedLocale;
      }

      if (document?.documentElement) {
        document.documentElement.lang = resolvedLocale;
      }
    },
    setupIdleSessionManager() {
      this.destroyIdleSessionManager();

      this.idleSessionManager = createIdleSessionManager({
        timeoutMs: this.idleLogoutMinutes * 60 * 1000,
        onWarningChange: ({ visible, remainingMs }) => {
          this.showSessionTimeoutBanner = visible;
          this.sessionTimeoutRemainingMs = remainingMs;
        },
        onTimeout: () => {
          if (this.idleLogoutInProgress) {
            return;
          }

          this.idleLogoutInProgress = true;
          this.showSessionTimeoutBanner = false;
          this.sessionTimeoutRemainingMs = 0;

          Auth.logout().catch(() => {
            clearCookiesOnLogout();
          });
        },
      });

      this.idleSessionManager.start();
    },
    destroyIdleSessionManager() {
      this.idleSessionManager?.stop();
      this.idleSessionManager = null;
      this.showSessionTimeoutBanner = false;
      this.sessionTimeoutRemainingMs = 0;
      this.idleLogoutInProgress = false;
    },
    keepSessionActive() {
      this.idleSessionManager?.markActive({ forceSync: true });
    },
    setupSingleActiveTabGuard() {
      this.destroySingleActiveTabGuard();

      this.singleActiveTabManager = createSingleActiveTabManager({
        onStateChange: ({ isBlocked }) => {
          this.isBlockedByAnotherTab = isBlocked;
        },
      });

      this.singleActiveTabManager.start();
    },
    destroySingleActiveTabGuard() {
      this.singleActiveTabManager?.stop();
      this.singleActiveTabManager = null;
      this.isBlockedByAnotherTab = false;
    },
    takeOverActiveTab() {
      this.singleActiveTabManager?.takeOver();
    },
    async initializeAccount() {
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      const account = this.getAccount(this.currentAccountId);
      const { locale, latest_chatwoot_version: latestChatwootVersion } =
        account;
      const { pubsub_token: pubsubToken } = this.currentUser || {};
      // If user locale is set, use it; otherwise use account locale
      this.setLocale(this.uiSettings?.locale || locale);
      this.latestChatwootVersion = latestChatwootVersion;
      vueActionCable.init(this.store, pubsubToken);
      this.reconnectService = new ReconnectService(this.store, this.router);
      window.reconnectService = this.reconnectService;

      verifyServiceWorkerExistence(registration =>
        registration.pushManager.getSubscription().then(subscription => {
          if (subscription) {
            registerSubscription();
          }
        })
      );
    },
  },
};
</script>

<template>
  <div
    v-if="!authUIFlags.isFetching"
    id="app"
    class="flex flex-col w-full h-screen min-h-0 bg-n-background"
    :dir="isRTL ? 'rtl' : 'ltr'"
  >
    <UpdateBanner :latest-chatwoot-version="latestChatwootVersion" />
    <StatusBanner />
    <SessionTimeoutBanner
      v-if="showSessionTimeoutBanner"
      :remaining-ms="sessionTimeoutRemainingMs"
      @stay-connected="keepSessionActive"
    />
    <template v-if="currentAccountId">
      <PendingEmailVerificationBanner v-if="hideOnOnboardingView" />
      <PaymentPendingBanner v-if="hideOnOnboardingView" />
      <LowBackupCodesBanner v-if="hideOnOnboardingView" />
    </template>
    <SingleTabBlocker
      v-if="isBlockedByAnotherTab"
      @take-over="takeOverActiveTab"
    />
    <router-view v-else v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
    <WootSnackbarBox />
    <NetworkNotification />
  </div>
  <LoadingState v-else />
</template>

<style lang="scss">
@import './assets/scss/app';

.v-popper--theme-tooltip .v-popper__inner {
  background: black !important;
  font-size: 0.75rem;
  padding: 4px 8px !important;
  border-radius: 6px;
  font-weight: 400;
}

.v-popper--theme-tooltip .v-popper__arrow-container {
  display: none;
}
</style>
